/**
 * Server-Sent Events client for streaming scope generation.
 *
 * The backend `/scope/generate/stream` endpoint emits one `stage` event as
 * each pipeline stage starts, then a single `complete` event with the full
 * ScopeGenerationResponse (or an `error` event). EventSource can't send a POST
 * body, so we use fetch + a ReadableStream reader and parse SSE frames by hand.
 *
 * Auth mirrors the axios interceptor in api.ts (Firebase id token, or the dev
 * bypass token when enabled).
 */
import { auth } from './firebase';
import { ScopeGenerationRequest, ScopeGenerationResponse } from '../types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

type StreamEvent =
  | { event: 'stage'; data: { stage: string } }
  | { event: 'complete'; data: ScopeGenerationResponse }
  | { event: 'error'; data: { detail: string } }
  | { event: string; data: unknown };

async function authHeader(): Promise<string | undefined> {
  const user = auth.currentUser;
  if (user) {
    try {
      return `Bearer ${await user.getIdToken()}`;
    } catch {
      /* fall through to bypass / unauthenticated */
    }
  }
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') {
    return 'Bearer dev-bypass-token';
  }
  return undefined;
}

function parseFrame(raw: string): StreamEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) } as StreamEvent;
  } catch {
    return null;
  }
}

/**
 * Stream a scope generation, invoking `onStage` as each pipeline stage starts.
 * Resolves with the final ScopeGenerationResponse, or rejects on transport /
 * pipeline error (the caller can then fall back to the non-streaming call).
 */
export async function streamScopeGeneration(
  request: ScopeGenerationRequest,
  onStage: (stage: string) => void,
): Promise<ScopeGenerationResponse> {
  const auth = await authHeader();
  const resp = await fetch(`${BASE_URL}/scope/generate/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(auth && { Authorization: auth }),
    },
    body: JSON.stringify(request),
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`Scope stream failed: HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ScopeGenerationResponse | null = null;
  let streamError: string | null = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const ev = parseFrame(frame);
      if (!ev) continue;
      if (ev.event === 'stage') {
        onStage((ev.data as { stage: string }).stage);
      } else if (ev.event === 'complete') {
        result = ev.data as ScopeGenerationResponse;
      } else if (ev.event === 'error') {
        streamError = (ev.data as { detail: string }).detail || 'Generation failed';
      }
    }
  }

  if (streamError) throw new Error(streamError);
  if (!result) throw new Error('Scope stream ended without a result');
  return result;
}
